import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTexts } from '../context/TextsContext';
import './Dashboard.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FRONTEND_URL = process.env.REACT_APP_BACKEND_URL;

// Language options for gift recipient
const LANGUAGES = [
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'pt', name: 'Português', flag: '🇵🇹' },
  { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
  { code: 'pl', name: 'Polski', flag: '🇵🇱' },
  { code: 'tr', name: 'Türkçe', flag: '🇹🇷' }
];

// Share method options
const SHARE_METHODS = [
  { id: 'email', name: 'Email', icon: '📧', requiresEmail: true },
  { id: 'whatsapp', name: 'WhatsApp', icon: '💬', requiresEmail: false },
  { id: 'facebook', name: 'Facebook', icon: '📘', requiresEmail: false },
  { id: 'linkedin', name: 'LinkedIn', icon: '💼', requiresEmail: false }
];

const Dashboard = () => {
  const { user, logout, loading: authLoading, isAuthenticated, checkAuth } = useAuth();
  const { t } = useTexts();
  const [candles, setCandles] = useState([]);
  const [sentCandles, setSentCandles] = useState([]); // Candles sent/gifted by user
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);
  const [stats, setStats] = useState({ total: 0, totalValue: 0, small: 0, medium: 0, large: 0, tealight: 0 });
  const [editingCandle, setEditingCandle] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', message: '' });
  const [meditationCandle, setMeditationCandle] = useState(null);
  const [showFreeCandleModal, setShowFreeCandleModal] = useState(false);
  const [activeTab, setActiveTab] = useState('my'); // 'my' or 'sent'
  const [freeCandleForm, setFreeCandleForm] = useState({
    name: '', message: '', recipient_email: '', recipient_name: '', gift_message: '',
    recipient_language: 'de', share_method: 'email'
  });
  const [freeCandleLimits, setFreeCandleLimits] = useState(null);
  const [sendingFreeCandle, setSendingFreeCandle] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Check auth if not passed from callback
  useEffect(() => {
    if (!location.state?.user && !isAuthenticated && !authLoading) {
      checkAuth();
    }
  }, [location.state, isAuthenticated, authLoading, checkAuth]);

  // Load user's candles
  useEffect(() => {
    if (isAuthenticated && user) {
      loadUserCandles();
    }
  }, [isAuthenticated, user]);

  // Redirect if not authenticated (after loading check)
  useEffect(() => {
    if (!authLoading && !isAuthenticated && !location.state?.user) {
      navigate('/login', { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate, location.state]);

  const loadUserCandles = async () => {
    try {
      const response = await axios.get(`${API}/candles/user`, {
        withCredentials: true
      });
      setCandles(response.data);
      
      // Calculate stats
      const total = response.data.length;
      const totalValue = response.data.reduce((sum, c) => sum + (c.amount || 0), 0) / 100;
      const small = response.data.filter(c => c.amount === 200).length;
      const medium = response.data.filter(c => c.amount === 500).length;
      const large = response.data.filter(c => c.amount === 1000).length;
      const tealight = response.data.filter(c => c.size === 'tealight' || c.is_free).length;
      setStats({ total, totalValue, small, medium, large, tealight });
      
      // Load free candle limits and sent candles
      loadFreeCandleLimits();
      loadSentCandles();
    } catch (error) {
      console.error('Error loading candles:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSentCandles = async () => {
    try {
      const response = await axios.get(`${API}/candles/sent`, { withCredentials: true });
      setSentCandles(response.data);
    } catch (error) {
      console.error('Error loading sent candles:', error);
    }
  };

  const loadFreeCandleLimits = async () => {
    try {
      const response = await axios.get(`${API}/free-candles/limits`, { withCredentials: true });
      setFreeCandleLimits(response.data);
    } catch (error) {
      console.error('Error loading free candle limits:', error);
    }
  };

  const sendFreeCandle = async () => {
    const shareMethod = freeCandleForm.share_method;
    
    // Email requires email address
    if (shareMethod === 'email' && !freeCandleForm.recipient_email.trim()) {
      alert('Bitte Email des Empfängers angeben');
      return;
    }
    
    if (!freeCandleForm.name.trim()) {
      alert('Bitte Name der Kerze angeben');
      return;
    }
    
    // For social sharing, use placeholder email
    const payload = {
      ...freeCandleForm,
      recipient_email: shareMethod === 'email' 
        ? freeCandleForm.recipient_email 
        : `social-${Date.now()}@placeholder.local`
    };
    
    setSendingFreeCandle(true);
    try {
      const response = await axios.post(`${API}/free-candles/send`, payload, { withCredentials: true });
      
      // Handle social sharing
      if (shareMethod !== 'email' && response.data.share_url) {
        const shareUrl = response.data.share_url;
        const shareMessage = response.data.share_message || `🕯️ Ein Geschenk für dich: ${shareUrl}`;
        
        let socialUrl = '';
        switch (shareMethod) {
          case 'whatsapp':
            socialUrl = `https://wa.me/?text=${encodeURIComponent(shareMessage)}`;
            break;
          case 'facebook':
            socialUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareMessage)}`;
            break;
          case 'linkedin':
            socialUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
            break;
          default:
            break;
        }
        
        if (socialUrl) {
          window.open(socialUrl, '_blank', 'width=600,height=400');
        }
        
        alert('Kerze erstellt! Du kannst sie jetzt teilen.');
      } else {
        alert('Kostenlose Kerze erfolgreich gesendet!');
      }
      
      setShowFreeCandleModal(false);
      setFreeCandleForm({ 
        name: '', message: '', recipient_email: '', recipient_name: '', gift_message: '',
        recipient_language: 'de', share_method: 'email'
      });
      loadFreeCandleLimits();
      loadSentCandles();
    } catch (error) {
      alert(error.response?.data?.detail || 'Fehler beim Senden');
    } finally {
      setSendingFreeCandle(false);
    }
  };

  // Upgrade free candle to paid
  const upgradeCandle = (candleId) => {
    // Navigate to home with upgrade params
    navigate(`/?upgrade=${candleId}`);
  };

  const togglePrivacy = async (candleId, currentPrivacy) => {
    try {
      await axios.patch(`${API}/candles/${candleId}/privacy`, 
        { is_private: !currentPrivacy },
        { withCredentials: true }
      );
      setCandles(candles.map(c => 
        c.id === candleId ? { ...c, is_private: !currentPrivacy } : c
      ));
    } catch (error) {
      console.error('Error toggling privacy:', error);
    }
  };

  const copyShareLink = (candleId) => {
    const shareUrl = `${window.location.origin}/candle/${candleId}`;
    navigator.clipboard.writeText(shareUrl);
    setCopiedId(candleId);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const startEditing = (candle) => {
    setEditingCandle(candle.id);
    setEditForm({ name: candle.name, message: candle.message || '' });
  };

  const cancelEditing = () => {
    setEditingCandle(null);
    setEditForm({ name: '', message: '' });
  };

  const saveEdit = async (candleId) => {
    try {
      await axios.patch(`${API}/candles/${candleId}`, editForm, {
        withCredentials: true
      });
      setCandles(candles.map(c => 
        c.id === candleId ? { ...c, name: editForm.name, message: editForm.message } : c
      ));
      setEditingCandle(null);
    } catch (error) {
      console.error('Error updating candle:', error);
      alert('Fehler beim Speichern');
    }
  };

  // Meditation mode handlers
  const openMeditation = (candle) => {
    setMeditationCandle({
      id: candle.id,
      name: candle.name,
      message: candle.message,
      amount: candle.amount || 200,
      size: getCandleSizeClass(candle.amount)
    });
  };

  const closeMeditation = () => {
    setMeditationCandle(null);
  };

  const getCandleSizeClass = (amount) => {
    if (amount === 200) return 'small';
    if (amount === 500) return 'medium';
    return 'large';
  };

  const handleLogout = async () => {
    await logout();
    navigate('/', { replace: true });
  };

  const getCandleSize = (amount) => {
    if (amount === 200) return 'Klein';
    if (amount === 500) return 'Mittel';
    return 'Groß';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Show loading while checking auth
  if (authLoading || (!user && !location.state?.user)) {
    return (
      <div className="dashboard-loading">
        <div className="dashboard-spinner"></div>
        <p>{t('dashboard.loading', 'Lädt...')}</p>
      </div>
    );
  }

  const currentUser = user || location.state?.user;

  return (
    <div className="dashboard">
      <div className="dashboard-ambient"></div>
      
      <header className="dashboard-header">
        <div className="dashboard-header-content">
          <button 
            className="dashboard-home-link"
            onClick={() => navigate('/')}
            data-testid="dashboard-home-link"
          >
            ← {t('nav.logo', 'Place of Lights')}
          </button>
          
          <div className="dashboard-user-section">
            {currentUser?.picture && (
              <img 
                src={currentUser.picture} 
                alt={currentUser.name}
                className="dashboard-avatar"
              />
            )}
            <span className="dashboard-user-name">{currentUser?.name}</span>
            <button 
              className="dashboard-logout-button"
              onClick={handleLogout}
              data-testid="dashboard-logout-button"
            >
              {t('nav.logout', 'Abmelden')}
            </button>
          </div>
        </div>
      </header>

      <main className="dashboard-main">
        {/* Top Section: Title + CTAs + Stats */}
        <div className="dashboard-top-section">
          <div className="dashboard-title-cta">
            <div className="dashboard-title-section">
              <h1 className="dashboard-title">{t('dashboard.title', 'Meine Kerzen')}</h1>
              <p className="dashboard-subtitle">
                {t('dashboard.subtitle', 'Verwalte deine leuchtenden Erinnerungen')}
              </p>
            </div>
            
            {/* Primary CTAs */}
            <div className="dashboard-cta-primary">
              <button 
                className="cta-buy-candle"
                onClick={() => navigate('/')}
                data-testid="dashboard-buy-candle"
              >
                <span className="cta-icon">🕯️</span>
                <span className="cta-text">{t('dashboard.add_candle', 'Neue Kerze kaufen')}</span>
              </button>
              
              {freeCandleLimits && freeCandleLimits.can_send && (
                <button 
                  className="cta-gift-candle"
                  onClick={() => setShowFreeCandleModal(true)}
                  data-testid="free-candle-button"
                >
                  <span className="cta-icon">🎁</span>
                  <span className="cta-text">{t('dashboard.free_candle', 'Kerze verschenken')}</span>
                  <span className="cta-badge">{freeCandleLimits.daily_remaining}/{freeCandleLimits.daily_limit}</span>
                </button>
              )}
            </div>
          </div>
          
          {/* Compact Stats */}
          {!loading && candles.length > 0 && (
            <div className="dashboard-stats-compact">
              <div className="stats-total">
                <span className="stats-number">{stats.total}</span>
                <span className="stats-label">{t('dashboard.stats.total', 'Kerzen')}</span>
              </div>
              <div className="stats-breakdown-compact">
                <div className="breakdown-item">
                  <span className="breakdown-count">{stats.small}</span>
                  <span className="breakdown-label">{t('dashboard.stats.small', 'Klein')}</span>
                </div>
                <div className="breakdown-item">
                  <span className="breakdown-count">{stats.medium}</span>
                  <span className="breakdown-label">{t('dashboard.stats.medium', 'Mittel')}</span>
                </div>
                <div className="breakdown-item">
                  <span className="breakdown-count">{stats.large}</span>
                  <span className="breakdown-label">{t('dashboard.stats.large', 'Groß')}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Tabs for My Candles vs Sent Candles */}
        <div className="dashboard-tabs">
          <button 
            className={`dashboard-tab ${activeTab === 'my' ? 'active' : ''}`}
            onClick={() => setActiveTab('my')}
            data-testid="tab-my-candles"
          >
            🕯️ {t('dashboard.tab.my_candles', 'Meine Kerzen')} ({candles.length})
          </button>
          <button 
            className={`dashboard-tab ${activeTab === 'sent' ? 'active' : ''}`}
            onClick={() => setActiveTab('sent')}
            data-testid="tab-sent-candles"
          >
            🎁 {t('dashboard.tab.sent_candles', 'Verschenkte Kerzen')} ({sentCandles.length})
          </button>
        </div>

        {loading ? (
          <div className="dashboard-content-loading">
            <div className="dashboard-spinner"></div>
          </div>
        ) : activeTab === 'my' ? (
          // MY CANDLES TAB
          candles.length === 0 ? (
            <div className="dashboard-empty">
              <div className="dashboard-empty-glow"></div>
              <h2>{t('dashboard.empty_title', 'Noch keine Kerzen angezündet')}</h2>
              <p>{t('dashboard.empty_subtitle', 'Deine erste Kerze wartet darauf, entzündet zu werden')}</p>
              <button 
                className="dashboard-light-button"
                onClick={() => navigate('/')}
                data-testid="dashboard-light-candle-button"
              >
                {t('dashboard.empty_button', 'Erste Kerze anzünden')}
              </button>
            </div>
          ) : (
            <div className="dashboard-candles-grid">
              {candles.map((candle) => (
                <div key={candle.id} className={`dashboard-candle-card ${candle.is_free ? 'tealight-card' : ''}`} data-testid="dashboard-candle-card">
                  <div className="candle-card-flame"></div>
                  
                  {/* Free candle upgrade badge */}
                  {candle.is_free && (
                    <div className="candle-upgrade-badge" onClick={() => upgradeCandle(candle.id)}>
                      ⬆️ {t('dashboard.upgrade', 'Upgrade')}
                    </div>
                  )}
                  
                  {editingCandle === candle.id ? (
                    <div className="candle-card-edit">
                      <input
                        type="text"
                        value={editForm.name}
                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                        placeholder={t('dashboard.edit.name_placeholder', 'Für wen leuchtet das Licht?')}
                        maxLength={50}
                      />
                      <textarea
                        value={editForm.message}
                        onChange={(e) => setEditForm({ ...editForm, message: e.target.value })}
                        placeholder={t('dashboard.edit.message_placeholder', 'Botschaft (optional)')}
                        maxLength={200}
                        rows={2}
                      />
                      <div className="edit-actions">
                        <button className="save-btn" onClick={() => saveEdit(candle.id)}>
                          ✓ {t('dashboard.edit.save', 'Speichern')}
                        </button>
                        <button className="cancel-btn" onClick={cancelEditing}>
                          ✗ {t('dashboard.edit.cancel', 'Abbrechen')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="candle-card-content">
                      <h3 className="candle-card-name">{candle.name}</h3>
                      {candle.message && (
                        <p className="candle-card-message">"{candle.message}"</p>
                      )}
                      
                      <div className="candle-card-meta">
                        <span className="candle-card-size">
                          {candle.is_free ? '🕯️ Teelicht' : getCandleSize(candle.amount)}
                        </span>
                        <span className="candle-card-date">{formatDate(candle.created_at)}</span>
                      </div>
                      
                      {/* Show if received as gift */}
                      {(candle.accepted_by_user_id || candle.claimed_by_user_id) && candle.sender_name && (
                        <div className="candle-received-badge">
                          🎁 {t('dashboard.card.received_from', 'Von')} {candle.sender_name}
                        </div>
                      )}
                      
                      {/* Show expiry for free candles */}
                      {candle.is_free && candle.expires_at && (
                        <div className="candle-expiry-info">
                          ⏱️ {t('dashboard.card.expires', 'Brennt bis')} {formatDate(candle.expires_at)}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="candle-card-actions">
                    <button 
                      className="candle-action-button view"
                      onClick={() => openMeditation(candle)}
                      data-testid={`view-candle-${candle.id}`}
                    >
                      🕯️ {t('dashboard.action.view', 'Betrachten')}
                    </button>
                    
                    <button 
                      className="candle-action-button edit"
                      onClick={() => startEditing(candle)}
                      data-testid={`edit-candle-${candle.id}`}
                    >
                      ✏️ {t('dashboard.action.edit', 'Bearbeiten')}
                    </button>
                    
                    <button 
                      className={`candle-action-button share ${copiedId === candle.id ? 'copied' : ''}`}
                      onClick={() => copyShareLink(candle.id)}
                      data-testid={`share-candle-${candle.id}`}
                    >
                      {copiedId === candle.id ? `✓ ${t('dashboard.action.copied', 'Kopiert!')}` : `🔗 ${t('dashboard.action.share', 'Teilen')}`}
                    </button>
                    
                    <button 
                      className={`candle-action-button privacy ${candle.is_private ? 'private' : 'public'}`}
                      onClick={() => togglePrivacy(candle.id, candle.is_private)}
                      data-testid={`privacy-candle-${candle.id}`}
                    >
                      {candle.is_private ? `🔒 ${t('dashboard.action.private', 'Privat')}` : `🌍 ${t('dashboard.action.public', 'Öffentlich')}`}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          // SENT CANDLES TAB
          sentCandles.length === 0 ? (
            <div className="dashboard-empty">
              <div className="dashboard-empty-glow"></div>
              <h2>{t('dashboard.sent.empty_title', 'Noch keine Kerzen verschenkt')}</h2>
              <p>{t('dashboard.sent.empty_subtitle', 'Verschenke Freude mit einem kostenlosen Teelicht')}</p>
              <button 
                className="dashboard-light-button"
                onClick={() => setShowFreeCandleModal(true)}
                data-testid="dashboard-gift-candle-button"
              >
                🎁 {t('dashboard.sent.empty_button', 'Erste Kerze verschenken')}
              </button>
            </div>
          ) : (
            <div className="dashboard-candles-grid">
              {sentCandles.map((candle) => (
                <div key={candle.id} className="dashboard-candle-card sent-card" data-testid="sent-candle-card">
                  <div className="candle-card-flame"></div>
                  
                  <div className="candle-card-content">
                    <h3 className="candle-card-name">{candle.name}</h3>
                    {candle.message && (
                      <p className="candle-card-message">"{candle.message}"</p>
                    )}
                    
                    <div className="candle-card-meta">
                      <span className="candle-card-size">
                        {candle.is_free ? '🕯️ Teelicht' : getCandleSize(candle.amount)}
                      </span>
                      <span className="candle-card-date">{formatDate(candle.created_at)}</span>
                    </div>
                    
                    {/* Recipient info */}
                    <div className="candle-recipient-info">
                      🎁 {t('dashboard.sent.to', 'An')}: {candle.recipient_name || candle.recipient_email}
                    </div>
                    
                    {/* Status badge */}
                    <div className={`candle-status-badge ${candle.is_accepted || candle.gift_claimed ? 'accepted' : 'pending'}`}>
                      {candle.is_accepted || candle.gift_claimed 
                        ? `✅ ${t('dashboard.sent.accepted', 'Angenommen')}${candle.accepted_by_name ? ` von ${candle.accepted_by_name}` : ''}`
                        : `⏳ ${t('dashboard.sent.pending', 'Ausstehend')}`
                      }
                    </div>
                    
                    {/* Show expiry for free candles */}
                    {candle.is_free && candle.expires_at && (
                      <div className="candle-expiry-info">
                        ⏱️ {candle.is_accepted ? t('dashboard.card.expires', 'Brennt bis') : t('dashboard.sent.expires_if_not_accepted', 'Ablauf wenn nicht angenommen')}: {formatDate(candle.expires_at)}
                      </div>
                    )}
                  </div>

                  <div className="candle-card-actions">
                    <button 
                      className="candle-action-button view"
                      onClick={() => openMeditation(candle)}
                    >
                      🕯️ {t('dashboard.action.view', 'Betrachten')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </main>

      {/* Free Candle Modal */}
      {showFreeCandleModal && (
        <div className="free-candle-modal-overlay" onClick={() => setShowFreeCandleModal(false)}>
          <div className="free-candle-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowFreeCandleModal(false)}>×</button>
            
            <h2>🎁 {t('dashboard.free_modal.title', 'Kostenlose Kerze verschenken')}</h2>
            <p className="modal-subtitle">
              {t('dashboard.free_modal.subtitle', 'Schenke jemandem ein Teelicht, das')} {freeCandleLimits?.duration_days || 10} {t('dashboard.free_modal.days_burns', 'Tage brennt')}.
            </p>
            
            <div className="free-candle-form">
              {/* Share Method Selection */}
              <div className="form-group">
                <label>{t('dashboard.free_modal.share_via', 'Senden via')}</label>
                <div className="share-method-options">
                  {SHARE_METHODS.map(method => (
                    <button
                      key={method.id}
                      type="button"
                      className={`share-method-btn ${freeCandleForm.share_method === method.id ? 'active' : ''}`}
                      onClick={() => setFreeCandleForm({...freeCandleForm, share_method: method.id})}
                    >
                      <span className="share-icon">{method.icon}</span>
                      <span className="share-name">{method.name}</span>
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="form-group">
                <label>{t('dashboard.free_modal.candle_name', 'Name der Kerze')} *</label>
                <input
                  type="text"
                  placeholder={t('dashboard.free_modal.candle_name_placeholder', 'z.B. Für meine Oma')}
                  value={freeCandleForm.name}
                  onChange={(e) => setFreeCandleForm({...freeCandleForm, name: e.target.value})}
                  data-testid="free-candle-name"
                />
              </div>
              
              <div className="form-group">
                <label>{t('dashboard.free_modal.candle_message', 'Nachricht (optional)')}</label>
                <textarea
                  placeholder={t('dashboard.free_modal.candle_message_placeholder', 'Eine persönliche Nachricht für die Kerze...')}
                  value={freeCandleForm.message}
                  onChange={(e) => setFreeCandleForm({...freeCandleForm, message: e.target.value})}
                  rows={2}
                  data-testid="free-candle-message"
                />
              </div>
              
              {/* Email field - only for email method */}
              {freeCandleForm.share_method === 'email' && (
                <div className="form-row">
                  <div className="form-group">
                    <label>{t('dashboard.free_modal.recipient_email', 'Email des Empfängers')} *</label>
                    <input
                      type="email"
                      placeholder="empfaenger@email.de"
                      value={freeCandleForm.recipient_email}
                      onChange={(e) => setFreeCandleForm({...freeCandleForm, recipient_email: e.target.value})}
                      data-testid="free-candle-recipient-email"
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('dashboard.free_modal.recipient_name', 'Name des Empfängers')}</label>
                    <input
                      type="text"
                      placeholder="Max Mustermann"
                      value={freeCandleForm.recipient_name}
                      onChange={(e) => setFreeCandleForm({...freeCandleForm, recipient_name: e.target.value})}
                      data-testid="free-candle-recipient-name"
                    />
                  </div>
                </div>
              )}
              
              {/* Language selection - for email method */}
              {freeCandleForm.share_method === 'email' && (
                <div className="form-group">
                  <label>{t('dashboard.free_modal.recipient_language', 'Sprache der Email')}</label>
                  <select
                    value={freeCandleForm.recipient_language}
                    onChange={(e) => setFreeCandleForm({...freeCandleForm, recipient_language: e.target.value})}
                    className="language-select"
                  >
                    {LANGUAGES.map(lang => (
                      <option key={lang.code} value={lang.code}>
                        {lang.flag} {lang.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              
              <div className="form-group">
                <label>{t('dashboard.free_modal.gift_message', 'Persönliche Nachricht an den Empfänger')}</label>
                <textarea
                  placeholder={t('dashboard.free_modal.gift_message_placeholder', 'Eine liebe Nachricht für den Empfänger...')}
                  value={freeCandleForm.gift_message}
                  onChange={(e) => setFreeCandleForm({...freeCandleForm, gift_message: e.target.value})}
                  rows={3}
                  data-testid="free-candle-gift-message"
                />
              </div>
              
              <div className="free-candle-info">
                {freeCandleForm.share_method === 'email' ? (
                  <p>📧 {t('dashboard.free_modal.info_email', 'Der Empfänger erhält eine Email mit dem Geschenk.')}</p>
                ) : (
                  <p>📲 {t('dashboard.free_modal.info_social', 'Es öffnet sich ein Fenster zum Teilen.')}</p>
                )}
                <p>⏱️ {t('dashboard.free_modal.info_duration', 'Das Teelicht brennt')} {freeCandleLimits?.duration_days || 10} {t('dashboard.free_modal.days', 'Tage')}.</p>
                <p>✅ {t('dashboard.free_modal.info_extend', 'Nach Annahme kann der Empfänger die Brenndauer verlängern.')}</p>
              </div>
              
              <button 
                className="send-free-candle-button"
                onClick={sendFreeCandle}
                disabled={sendingFreeCandle}
                data-testid="send-free-candle"
              >
                {sendingFreeCandle 
                  ? t('dashboard.free_modal.sending', 'Wird gesendet...') 
                  : freeCandleForm.share_method === 'email'
                    ? `📧 ${t('dashboard.free_modal.send_email', 'Per Email senden')}`
                    : `${SHARE_METHODS.find(m => m.id === freeCandleForm.share_method)?.icon || '🎁'} ${t('dashboard.free_modal.share_via', 'Teilen via')} ${SHARE_METHODS.find(m => m.id === freeCandleForm.share_method)?.name || ''}`
                }
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Meditation/Contemplation Overlay */}
      {meditationCandle && (
        <div className="meditation-overlay" onClick={closeMeditation}>
          <div className="meditation-content" onClick={(e) => e.stopPropagation()}>
            <button className="meditation-close" onClick={closeMeditation} data-testid="meditation-close">
              ×
            </button>
            
            {/* Realistic Candle */}
            <div className={`meditation-candle ${getCandleSizeClass(meditationCandle.amount)}`}>
              <div className="med-candle-wax">
                <div className="med-wax-drip"></div>
                <div className="med-wax-drip delay-1"></div>
              </div>
              <div className="med-wick"></div>
              <div className="med-flame">
                <div className="med-flame-outer"></div>
                <div className="med-flame-middle"></div>
                <div className="med-flame-inner"></div>
                <div className="med-flame-core"></div>
              </div>
              <div className="med-glow"></div>
            </div>
            
            {/* Candle Info */}
            <div className="meditation-info">
              <h2 className="meditation-name">{meditationCandle.name}</h2>
              {meditationCandle.message && (
                <p className="meditation-message">"{meditationCandle.message}"</p>
              )}
            </div>
            
            <p className="meditation-hint">{t('meditation.hint', 'Nimm dir einen Moment der Stille...')}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
